<template>
    <div
        :style="backgroundColor ? { backgroundColor: backgroundColor.hex } : {}"
        class="padding-normal flex w-full flex-col justify-center space-y-5 lg:flex-row lg:space-x-5 lg:space-y-0"
        :class="textColorClass">
        <div class="normal-container !flex flex-col space-y-6 md:flex-row md:space-x-5 md:space-y-0 lg:w-full">
            <!-- Left Content -->
            <BlockDoubleBannerItem
                v-if="left"
                :url="leftUrl"
                :item="left"
                :formatted-title="formattedLeftTitle"
                image-key="imageLeft" />

            <!-- Right Content -->
            <BlockDoubleBannerItem
                v-if="right"
                :url="rightUrl"
                :item="right"
                :formatted-title="formattedRightTitle"
                image-key="imageRight" />
        </div>
    </div>
</template>

<script setup lang="ts">
    interface ButtonType {
        title?: string;
        type?: string;
        internalLink?: { _ref: string };
        externalUrl?: string;
    }

    interface BannerItemType {
        title?: string;
        subtitle?: string;
        imageLeft?: any;
        imageRight?: any;
        button?: ButtonType;
    }

    const props = defineProps<{
        backgroundColor?: { hex: string };
        left?: BannerItemType;
        right?: BannerItemType;
        textCenter?: boolean;
        textColor?: string;
    }>();

    const textColorClass = computed(() => {
        return props.textColor === 'black' ? 'text-black' : 'text-white';
    });

    const formatTitle = (title?: string) => {
        if (!title) return '';
        const words = title.split(' ');
        let formatted = '';
        for (let i = 0; i < words.length; i++) {
            if (i > 0 && words[i].length > 5) {
                formatted += '<br>';
            }
            formatted += ' ' + words[i];
        }
        return formatted.trim();
    };

    const formattedLeftTitle = computed(() => formatTitle(props.left?.title));
    const formattedRightTitle = computed(() => formatTitle(props.right?.title));

    const leftUrl = ref('#');
    const rightUrl = ref('#');

    const getButtonUrl = async (button?: ButtonType): Promise<string> => {
        if (!button) return '#';

        if (button.internalLink?._ref) {
            try {
                const buttonData = await useGetSeoUrl([button.internalLink._ref]);
                const seoPathInfo = buttonData?.find((item: any) => item.id === button.internalLink._ref)?.seoUrls?.[0]
                    ?.seoPathInfo;
                return seoPathInfo ? `${seoPathInfo.toLowerCase()}` : '#';
            } catch (error) {
                console.error('Error fetching SEO URL:', error);
                return '#';
            }
        } else if (button.externalUrl) {
            return button.externalUrl;
        }
        return '#';
    };

    const fetchButtonData = async () => {
        try {
            const [leftUrlResult, rightUrlResult] = await Promise.all([
                getButtonUrl(props.left?.button),
                getButtonUrl(props.right?.button),
            ]);
            leftUrl.value = leftUrlResult;
            rightUrl.value = rightUrlResult;
        } catch (error) {
            console.error('Error fetching button data:', error);
        }
    };

    onMounted(() => {
        fetchButtonData();
    });

    watch(
        () => [props.left, props.right],
        () => {
            fetchButtonData();
        },
        { deep: true, immediate: true },
    );
</script>

<style scoped>
    /* Your styles here */
</style>
